import { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { useSnackbar } from "notistack";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import { getPreviewDetail, submitForm_v1 } from "src/DAL/Forms/forms";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrIcon from "src/design-system/components/GrIcon";
import GrButton from "src/design-system/components/GrButton";
import GrCard from "src/design-system/components/GrCard";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrPage from "src/design-system/components/GrPage";
import GrBadge from "src/design-system/components/GrBadge";
import GrChoiceChips from "src/design-system/components/GrChoiceChips";
import GrTextField from "src/design-system/components/GrTextField";
import GrFormQuestion from "src/design-system/components/GrFormQuestion";
import GrEmptyState from "src/design-system/components/GrEmptyState";
import GrLoader from "src/design-system/components/GrLoader";
import AvatarGallery from "./component/imageViewOnLength";

/**
 * Fill in a form (/forms-submition/:slug): one card of questions, Cancel + Submit.
 * Same API and payload as before (getPreviewDetail → submitForm_v1 with { questions }).
 */

const pageSx = { maxWidth: 760, mx: "auto" };
const backLinkSx = {
  color: gr.color.ink2,
  fontWeight: gr.font.weight.regular,
  "&:hover": { color: gr.color.brandDark },
};
const signatureBoxSx = {
  border: `1px solid ${gr.color.line}`,
  borderRadius: gr.radius.tile,
  background: gr.color.surface,
  overflow: "hidden",
  "& canvas": {
    display: "block",
    width: "100%",
    height: `calc(${gr.space[12]} * 3)`,
  },
};
const signatureActionsSx = { display: "flex", justifyContent: "flex-end", mt: gr.space[2] };
const footerSx = {
  display: "flex",
  justifyContent: "flex-end",
  gap: gr.space[3],
  mt: gr.space[6],
  "@media (max-width: 599.95px)": { "& > *": { flex: 1 } },
};

/** Has this question got an answer? (same rules the submit check uses) */
const isAnswered = (q) => {
  if (q.type === "image") {
    const files = (q.multiple_answers || []).filter(Boolean).length;
    return files > 0 && files >= (q.options_count || 1);
  }
  if (q.type === "multiple_select") return (q.multiple_answers || []).length > 0;
  return String(q.answer || "").trim() !== "";
};

/** First required question left empty → { index, message } (old checks, one message per type). */
const firstMissing = (questions) => {
  for (let index = 0; index < questions.length; index += 1) {
    const q = questions[index];
    if (!q?.required) continue;
    if (q.type === "image") {
      const files = q.multiple_answers || [];
      const all =
        files.length === q.options_count &&
        Array.from({ length: q.options_count || 0 }).every((_, i) => Boolean(files[i]));
      if (!all) return { index, message: "Please upload all images for this question." };
    } else if (q.type === "multiple_select") {
      if ((q.multiple_answers || []).length === 0) {
        return { index, message: "Please choose at least one answer." };
      }
    } else if (["free_text", "multiple_choice", "signature"].includes(q.type) && q.answer === "") {
      return {
        index,
        message: q.type === "signature" ? "Please sign here." : "Please answer this question.",
      };
    }
  }
  return null;
};

export default function UserInformationform() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { slug } = useParams();
  const [form, setForm] = useState(state || null);
  const [questions, setQuestions] = useState([]);
  const [loadState, setLoadState] = useState("loading"); // loading | ready | error
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null); // { index, message }
  const signatureRefs = useRef({});

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadState("loading");
      const result = await getPreviewDetail(slug);
      if (!alive) return;
      if (result?.code === 200) {
        const list = result?.form?.questions || [];
        // Image questions start with empty slots (as before).
        list.forEach((item) => {
          if (item.type === "image") {
            item.answer = [];
            item.multiple_answers = [];
            item.loading = [];
          }
        });
        setForm((current) => ({ ...(result.form || {}), ...(current || {}) }));
        setQuestions([...list]);
        setLoadState("ready");
      } else {
        enqueueSnackbar(result?.message || "Could not open this form", { variant: "error" });
        setLoadState("error");
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug, enqueueSnackbar]);

  const update = (index, patch) => {
    setQuestions((list) => list.map((q, i) => (i === index ? { ...q, ...patch } : q)));
    if (error?.index === index) setError(null);
  };

  const saveSignature = (index) => {
    const pad = signatureRefs.current[index];
    if (pad && !pad.isEmpty()) update(index, { answer: pad.toDataURL() });
  };
  const clearSignature = (index) => {
    signatureRefs.current[index]?.clear();
    update(index, { answer: "" });
  };

  const goBack = () => navigate("/forms");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = firstMissing(questions);
    if (missing) {
      setError(missing);
      enqueueSnackbar(`Please answer question ${missing.index + 1}`, { variant: "error" });
      document
        .getElementById(`form-q-${missing.index}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    // Uploaded images travel in multiple_answers; their answer / loading fields are not sent.
    const payload = questions.map((q) => {
      if (q.type !== "image") return q;
      const { loading, ...rest } = q;
      return { ...rest, answer: "" };
    });
    setSubmitting(true);
    const res = await submitForm_v1(slug, { questions: payload });
    if (res?.code === 200) {
      enqueueSnackbar(res.message, { variant: "success" });
      navigate("/forms");
    } else {
      setSubmitting(false);
      enqueueSnackbar(res?.message || "Could not submit this form", { variant: "error" });
    }
  };

  const answered = questions.filter(isAnswered).length;

  const control = (q, index) => {
    const label = q.title || `Question ${index + 1}`;
    const hasError = error?.index === index;
    if (q.type === "multiple_choice") {
      return (
        <GrChoiceChips
          ariaLabel={label}
          options={(q.options || []).map(String)}
          value={q.answer || ""}
          onChange={(value) => update(index, { answer: value })}
          error={hasError}
        />
      );
    }
    if (q.type === "multiple_select") {
      return (
        <GrChoiceChips
          multiple
          ariaLabel={label}
          options={(q.options || []).map(String)}
          value={q.multiple_answers || []}
          onChange={(value) => update(index, { multiple_answers: value })}
          error={hasError}
        />
      );
    }
    if (q.type === "free_text") {
      return (
        <GrTextField
          id={`form-input-${index}`}
          fullWidth
          multiline
          minRows={3}
          placeholder="Type your answer…"
          value={q.answer || ""}
          onChange={(e) => update(index, { answer: e.target.value })}
          error={hasError}
        />
      );
    }
    if (q.type === "image") {
      return (
        <AvatarGallery
          length={q.options_count}
          Index={index}
          questions={questions}
          Image={q.answer}
          // AvatarGallery passes the list it had when the upload started: take only this
          // question from it, so answers typed during the upload are kept.
          setQuestions={(next) => {
            setQuestions((list) => list.map((item, i) => (i === index ? next[index] : item)));
            if (error?.index === index) setError(null);
          }}
        />
      );
    }
    if (q.type === "signature") {
      return (
        <>
          <Box sx={signatureBoxSx}>
            <SignatureCanvas
              ref={(pad) => {
                signatureRefs.current[index] = pad;
              }}
              penColor="black"
              backgroundColor="white"
              // Keep the drawing when the screen resizes (phone rotate, keyboard).
              clearOnResize={false}
              onEnd={() => saveSignature(index)}
            />
          </Box>
          <Box sx={signatureActionsSx}>
            <GrButton variant="quiet" size="sm" onClick={() => clearSignature(index)}>
              Clear
            </GrButton>
          </Box>
        </>
      );
    }
    return null;
  };

  return (
    <GrPage>
      <Box sx={pageSx}>
        <Box sx={{ mb: gr.space[6] }}>
          <GrButton variant="link" onClick={goBack} sx={backLinkSx}>
            ← Your Information
          </GrButton>
        </Box>

        {loadState === "loading" ? (
          <GrLoader label="Opening the form" />
        ) : loadState === "error" ? (
          <GrCard>
            <GrEmptyState
              icon={<GrIcon icon={icons.file} />}
              title="This form could not be opened."
              action={
                <GrButton variant="secondary" onClick={goBack}>
                  Back to Your Information
                </GrButton>
              }
            />
          </GrCard>
        ) : (
          <>
            <GrPageHeader
              title={form?.title || "Form"}
              subtitle={form?.description || null}
              actions={
                questions.length ? (
                  <GrBadge tone={answered === questions.length ? "ok" : "neutral"}>
                    {answered} of {questions.length} answered
                  </GrBadge>
                ) : null
              }
            />
            <Box component="form" noValidate autoComplete="off" onSubmit={handleSubmit}>
              <GrCard>
                {questions.length === 0 ? (
                  <GrEmptyState compact title="This form has no questions." />
                ) : (
                  questions.map((q, index) => (
                    <GrFormQuestion
                      key={q._id || index}
                      id={`form-q-${index}`}
                      number={index + 1}
                      label={q.title || `Question ${index + 1}`}
                      required={Boolean(q.required)}
                      help={q.type === "image" ? q.options?.[0] : null}
                      htmlFor={q.type === "free_text" ? `form-input-${index}` : undefined}
                      error={error?.index === index ? error.message : null}
                    >
                      {control(q, index)}
                    </GrFormQuestion>
                  ))
                )}
              </GrCard>
              {questions.length ? (
                <Box sx={footerSx}>
                  <GrButton variant="quiet" onClick={goBack} disabled={submitting}>
                    Cancel
                  </GrButton>
                  <GrButton type="submit" loading={submitting}>
                    Submit
                  </GrButton>
                </Box>
              ) : null}
            </Box>
          </>
        )}
      </Box>
    </GrPage>
  );
}
